import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../utils/api'
import Navbar from '../components/Navbar'

export default function SubjectIntake() {
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [orderInfo, setOrderInfo] = useState<{ tier: string } | null>(null)

  // Form fields
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zip, setZip] = useState('')
  const [phone, setPhone] = useState('')
  const [additionalNames, setAdditionalNames] = useState('')

  // Authorization fields
  const [relationshipToSubject, setRelationshipToSubject] = useState('')
  const [relationshipDetails, setRelationshipDetails] = useState('')
  const [attestationAccepted, setAttestationAccepted] = useState(false)

  useEffect(() => {
    async function fetchOrder() {
      try {
        const response = await api.get(`/order/${orderId}`)
        setOrderInfo(response.data)
      } catch {
        setError('Order not found')
      }
    }
    if (orderId) fetchOrder()
  }, [orderId])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await api.post(`/order/${orderId}/subject`, {
        name,
        email,
        address,
        city,
        state,
        zip,
        phone,
        additional_names: additionalNames,
        relationship_to_subject: relationshipToSubject,
        relationship_details: relationshipDetails,
        attestation_accepted: attestationAccepted,
      })
      navigate('/dashboard?started=true')
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit. Please try again.')
      setLoading(false)
    }
  }

  const tierLabels: Record<string, string> = {
    quick: 'Quick Check',
    full: 'Full Assessment',
    family: 'Family Package',
  }

  return (
    <div className="min-h-screen">
      <Navbar />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="card">
            <div className="mb-6">
              <h1 className="text-2xl font-bold mb-2">Subject Information</h1>
              <p className="text-dark-400">
                Tell us who we're scanning. This information is encrypted and used only for your privacy assessment.
              </p>
              {orderInfo && (
                <p className="text-ginger-500 text-sm mt-2">
                  Order: {tierLabels[orderInfo.tier] || orderInfo.tier}
                </p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Primary Subject */}
              <div className="space-y-4">
                <h3 className="font-semibold text-dark-200 border-b border-dark-700 pb-2">
                  Primary Subject
                </h3>

                <div>
                  <label className="label">Full Legal Name *</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Michael Smith"
                    className="input"
                    required
                  />
                  <p className="text-dark-500 text-xs mt-1">
                    As it appears on legal documents
                  </p>
                </div>

                <div>
                  <label className="label">Email Address *</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="john@example.com"
                    className="input"
                    required
                  />
                </div>

                <div>
                  <label className="label">Current Address</label>
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="123 Main Street"
                    className="input"
                  />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <label className="label">City</label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Boston"
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="label">State</label>
                    <input
                      type="text"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      placeholder="MA"
                      className="input"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <label className="label">ZIP</label>
                    <input
                      type="text"
                      value={zip}
                      onChange={(e) => setZip(e.target.value)}
                      placeholder="02101"
                      className="input"
                      maxLength={10}
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Phone Number</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="input"
                  />
                </div>

                <div>
                  <label className="label">Other Names / Aliases</label>
                  <input
                    type="text"
                    value={additionalNames}
                    onChange={(e) => setAdditionalNames(e.target.value)}
                    placeholder="Maiden names, nicknames, previous names"
                    className="input"
                  />
                  <p className="text-dark-500 text-xs mt-1">
                    Include maiden names, nicknames, or any other names you've used
                  </p>
                </div>
              </div>

              {/* Authorization Verification */}
              <div className="space-y-4">
                <h3 className="font-semibold text-dark-200 border-b border-dark-700 pb-2">
                  Authorization Verification
                </h3>

                <div>
                  <label className="label">Your Relationship to Subject *</label>
                  <select
                    value={relationshipToSubject}
                    onChange={(e) => setRelationshipToSubject(e.target.value)}
                    className="input"
                    required
                  >
                    <option value="">Select relationship...</option>
                    <option value="self">This report is about myself</option>
                    <option value="employer">I am requesting this on behalf of my employer (with subject consent)</option>
                    <option value="security_provider">I am the subject's authorized security provider</option>
                    <option value="family_authorized">I am authorized family member (spouse, parent of minor)</option>
                    <option value="legal_representative">I am the subject's legal representative</option>
                    <option value="other">Other (please specify)</option>
                  </select>
                </div>

                {relationshipToSubject === 'other' && (
                  <div>
                    <label className="label">Please describe your relationship and authorization *</label>
                    <textarea
                      value={relationshipDetails}
                      onChange={(e) => setRelationshipDetails(e.target.value)}
                      placeholder="Describe your relationship to the subject and your authorization to request this report..."
                      className="input min-h-[100px]"
                      required
                    />
                  </div>
                )}

                {relationshipToSubject && relationshipToSubject !== 'self' && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-sm">
                    <div className="flex items-start gap-3">
                      <span className="text-yellow-400">&#9888;</span>
                      <div className="text-yellow-200">
                        <strong>Third-Party Reports:</strong> Reports requested on behalf of others require
                        documented authorization and may be subject to additional verification.
                        All orders are manually reviewed before release.
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Legal Attestation */}
              <div className="space-y-4">
                <h3 className="font-semibold text-dark-200 border-b border-dark-700 pb-2">
                  Legal Attestation
                </h3>

                <div className="bg-dark-800 rounded-lg p-4 text-sm space-y-3">
                  <p className="text-dark-300">
                    By checking the box below, I certify and attest that:
                  </p>
                  <ul className="text-dark-400 text-xs space-y-2 list-disc list-inside">
                    <li>I am authorized to request this privacy assessment for the named subject</li>
                    <li>I will use this report solely for legitimate privacy protection purposes</li>
                    <li>I will NOT use this report for stalking, harassment, intimidation, or any illegal purpose</li>
                    <li>I will NOT use this report to harm, threaten, or locate someone against their will</li>
                    <li>I understand that misuse of this information may constitute a criminal offense</li>
                    <li>I understand that Gingerbread Privacy reserves the right to decline any request and report suspected misuse to law enforcement</li>
                  </ul>

                  <div className="pt-3 border-t border-dark-700">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={attestationAccepted}
                        onChange={(e) => setAttestationAccepted(e.target.checked)}
                        className="mt-1 w-5 h-5 rounded border-dark-600 bg-dark-700 text-ginger-500 focus:ring-ginger-500"
                        required
                      />
                      <span className="text-dark-200">
                        I have read, understand, and agree to the above attestation. I certify under penalty
                        of perjury that my statements are true and accurate.
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Privacy Note */}
              <div className="bg-dark-800 rounded-lg p-4 text-sm">
                <div className="flex items-start gap-3">
                  <span className="text-green-400">&#128274;</span>
                  <div>
                    <p className="text-dark-300">
                      All information is encrypted at rest using AES-256 encryption.
                      Your data is never sold or shared with third parties.
                      Your IP address and attestation are logged for security purposes.
                    </p>
                  </div>
                </div>
              </div>

              {error && <p className="text-red-400 text-sm">{error}</p>}

              <button
                type="submit"
                disabled={loading || !name || !email || !relationshipToSubject || !attestationAccepted}
                className="btn btn-primary w-full"
              >
                {loading ? 'Starting Scan...' : 'Start Privacy Scan'}
              </button>

              <p className="text-dark-500 text-xs text-center">
                All orders are manually reviewed before report release.
                Suspicious requests may be declined and reported.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
